/**
 * DMS upload modal — used both standalone (from the DMS page) and embedded
 * (from Asset Details / Device Details with a pre-linked entity).
 */
import { useEffect, useState } from "react";
import { File as FileIcon, Loader2, Upload } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
  inferContentType,
  uploadFile,
  type DMSFolder,
  type LinkableEntityType,
} from "../../lib/dmsApi";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  /** Pre-selected folder (DMS page). */
  folder?: DMSFolder | null;
  /** Pre-resolved folder path (Asset Details flow auto-routes to /Assets/{tag}/). */
  defaultFolderPath?: string;
  /** Pre-linked entity (Asset Details / Device Details flow). */
  linkEntityType?: LinkableEntityType;
  linkEntityId?: string;
  /** Pre-existing document — if set, this upload becomes a new version. */
  newVersionOfDocumentId?: string;
  onUploaded?: (documentId: string) => void;
  title?: string;
}

export function UploadDocumentModal({
  isOpen,
  onClose,
  virtualSiteId,
  folder = null,
  defaultFolderPath,
  linkEntityType,
  linkEntityId,
  newVersionOfDocumentId,
  onUploaded,
  title,
}: UploadDocumentModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [folderPath, setFolderPath] = useState(defaultFolderPath ?? "");
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setName("");
      setDescription("");
      setTags("");
      setChangeNote("");
      setFolderPath(defaultFolderPath ?? "");
      setProgress(0);
      setError(null);
    }
  }, [isOpen, defaultFolderPath]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !name && !newVersionOfDocumentId) {
      // Pre-fill the logical-document name with the bare filename.
      const stem = f.name.replace(/\.[^.]+$/, "");
      setName(stem);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setError("Pick a file first.");
      return;
    }
    if (!newVersionOfDocumentId && !name.trim()) {
      setError("Document name is required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { document_id } = await uploadFile(
        {
          virtual_site_id: virtualSiteId,
          document_id: newVersionOfDocumentId,
          folder_id: folder?.id,
          folder_path: !folder && folderPath ? folderPath : undefined,
          name: newVersionOfDocumentId ? undefined : name.trim(),
          description: description.trim() || undefined,
          tags: tags.trim() || undefined,
          file_name: file.name,
          file_size_bytes: file.size,
          content_type: inferContentType(file),
          change_note: changeNote.trim() || undefined,
          link_entity_type: linkEntityType,
          link_entity_id: linkEntityId,
        },
        file,
        (frac) => setProgress(frac),
      );
      onUploaded?.(document_id);
      onClose();
    } catch (err) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg ?? "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  }

  const modalTitle =
    title ?? (newVersionOfDocumentId ? "Upload new version" : "Upload document");

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="md" opaque>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-surface-300 mb-1.5">File</label>
          <label className="flex items-center gap-3 rounded-lg border border-dashed border-white/15 bg-white/[0.03] px-4 py-6 cursor-pointer hover:bg-white/[0.06]">
            <Upload className="h-5 w-5 text-surface-400" />
            <div className="flex-1 min-w-0">
              {file ? (
                <>
                  <div className="text-sm text-surface-100 truncate">{file.name}</div>
                  <div className="text-xs text-surface-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB · {inferContentType(file)}
                  </div>
                </>
              ) : (
                <span className="text-sm text-surface-400">
                  Click to choose a file
                </span>
              )}
            </div>
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
        </div>

        {!newVersionOfDocumentId && (
          <>
            <div>
              <label className="block text-xs font-medium text-surface-300 mb-1.5">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Inspection Report"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-300 mb-1.5">
                Description (optional)
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-300 mb-1.5">
                Keywords / tags (optional, comma-separated)
              </label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="manual,2026,pump-17"
              />
            </div>
            {!folder && (
              <div>
                <label className="block text-xs font-medium text-surface-300 mb-1.5">
                  Folder path (optional)
                </label>
                <Input
                  value={folderPath}
                  onChange={(e) => setFolderPath(e.target.value)}
                  placeholder="Assets/Pump-01/Manuals"
                />
                <p className="mt-1 text-xs text-surface-500">
                  Slash-separated. Folders are created if they don't exist. Leave empty to upload to root.
                </p>
              </div>
            )}
            {folder && (
              <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-xs text-surface-400">
                Uploading into folder: <span className="text-surface-200">{folder.path}</span>
              </div>
            )}
          </>
        )}

        {newVersionOfDocumentId && (
          <div>
            <label className="block text-xs font-medium text-surface-300 mb-1.5">
              What changed in this version? (optional)
            </label>
            <Input
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="Updated torque values on page 4"
            />
          </div>
        )}

        {submitting && progress > 0 && progress < 1 && (
          <div className="space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-brand-500 transition-all"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <div className="text-xs text-surface-400">
              Uploading… {Math.round(progress * 100)}%
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" isLoading={submitting} disabled={!file}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading
              </>
            ) : (
              <>
                <FileIcon className="h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
